/** Hot wallets per chain, cap, cold address, "move to cold" (D50), sweep history, ledger reconciliation. */
import {
  Address,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ExternalLink,
  Field,
  Input,
  Notice,
  Row,
  Select,
  Skeleton,
  Spot,
  compareDecimalStrings,
  formatAmount,
  formatDateTime,
  normaliseAmountInput,
} from "@custos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Snowflake } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "../components/AdminShell";
import { SignerCard } from "../components/controls";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { usd } from "../lib/format";
import { useCan } from "../lib/permissions";
import { qk, useConfig, useHotWallets, useReconciliation } from "../lib/queries";

export function WalletsPage() {
  const hot = useHotWallets();
  const canCold = useCan()("cold.send");
  const [coldChain, setColdChain] = useState<string | null>(null);
  const [sentUrl, setSentUrl] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Wallets"
        subtitle={hot.data ? `Hot wallet cap per chain: ${usd(hot.data.capUsdCents, "none")}` : undefined}
      />
      {sentUrl && (
        <Notice tone="success" title="Sent to your cold wallet" className="mb-4">
          <ExternalLink href={sentUrl}>Track it on the explorer</ExternalLink> — the workers mark it
          confirmed.
        </Notice>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          {hot.isPending ? (
            <Skeleton className="h-48 rounded-card" />
          ) : hot.error ? (
            <Notice tone="danger">{errorMessage(hot.error)}</Notice>
          ) : hot.data.chains.length === 0 ? (
            <Card>
              <EmptyState icon={<Spot name="vault" />} title="No hot wallet balances yet">
                The workers record them every 5 minutes.
              </EmptyState>
            </Card>
          ) : (
            <ul className="stagger grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="hot-wallets">
              {hot.data.chains.map((c) => (
                <li key={c.chain}>
                  <Card className="flex h-full flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="capitalize">{c.chain}</CardTitle>
                      {c.overCap ? <Badge tone="danger">above cap</Badge> : <Badge tone="success">ok</Badge>}
                    </div>
                    <p className="text-3xl tracking-tight text-fg">{usd(c.totalUsdCents, "$?")}</p>
                    <div>
                      {c.assets.map((a) => (
                        <Row key={a.asset} label={a.asset}>
                          <span className="font-mono tabular-nums">{formatAmount(a.balance, 8)}</span>
                        </Row>
                      ))}
                    </div>
                    <div className="text-sm">
                      <p className="text-muted">Hot</p>
                      <Address value={c.address} start={8} end={6} />
                      <p className="mt-1 text-muted">Cold</p>
                      {c.coldAddress ? (
                        <Address value={c.coldAddress} start={8} end={6} />
                      ) : (
                        <span className="text-warning">not set</span>
                      )}
                    </div>
                    {canCold && (
                      <Button
                        variant={c.overCap ? "primary" : "secondary"}
                        size="sm"
                        className="mt-auto self-start"
                        disabled={!hot.data.coldControls}
                        onClick={() => setColdChain(c.chain)}
                      >
                        <Snowflake className="size-4" aria-hidden /> Move to cold
                      </Button>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
          {hot.data && !hot.data.coldControls && (
            <Notice tone="info">
              Moving to cold from the panel is off (no <code className="font-mono">SIGNER_ADMIN_SECRET</code>{" "}
              for the API). Use <code className="font-mono">pnpm admin:to-cold</code>.
            </Notice>
          )}
          {hot.data && hot.data.alerts.length > 0 && (
            <Card>
              <CardTitle className="mb-2">Wallet alerts</CardTitle>
              {hot.data.alerts.map((a) => (
                <p key={a.id} className="py-1 text-sm text-fg">
                  <Badge tone="warning" className="mr-2">
                    {a.type.replaceAll("_", " ").toLowerCase()}
                  </Badge>
                  {a.message}
                </p>
              ))}
            </Card>
          )}
        </div>
        <SignerCard />
      </div>

      <Transfers />
      <Reconciliation />
      <ColdModal chain={coldChain} onClose={() => setColdChain(null)} onSent={setSentUrl} />
    </>
  );
}

function Transfers() {
  const hot = useHotWallets();
  const rows = hot.data?.transfers ?? [];
  return (
    <section className="mt-6">
      <h2 className="mb-3 text-xl font-medium text-fg">Sweeps, gas top-ups and moves to cold</h2>
      {rows.length === 0 ? (
        <Card>
          <EmptyState icon={<Spot name="swap" />} title="None yet" />
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0 sm:p-0">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-muted">
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Kind</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Transaction</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 whitespace-nowrap text-muted">{formatDateTime(t.createdAt)}</td>
                  <td className="px-4 py-3 text-fg">
                    {t.kind === "TO_COLD" ? "to cold" : t.kind.toLowerCase()}
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums">
                    {formatAmount(t.amount, 8)} {t.asset} <span className="text-muted">· {t.chain}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      tone={
                        t.status === "CONFIRMED" ? "success" : t.status === "FAILED" ? "danger" : "primary"
                      }
                    >
                      {t.status.toLowerCase()}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {t.explorerUrl ? <ExternalLink href={t.explorerUrl}>Explorer</ExternalLink> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </section>
  );
}

function Reconciliation() {
  const rec = useReconciliation();
  return (
    <section className="mt-6">
      <h2 className="text-xl font-medium text-fg">Ledger vs chain</h2>
      <p className="mb-3 text-sm text-muted">
        What we owe (user balances + collected fees) next to what the hot wallet holds on chain. Deposits not
        yet swept sit on deposit addresses and aren't counted, so a small negative difference can be normal.
        Swap clearing is the net of all swaps: on testnet swaps only move ledger balances (no on-chain trade),
        so it explains the difference for swapped assets.
      </p>
      {rec.isPending ? (
        <Skeleton className="h-32 rounded-card" />
      ) : rec.error ? (
        <Notice tone="danger">{errorMessage(rec.error)}</Notice>
      ) : (
        <Card className="overflow-x-auto p-0 sm:p-0">
          <table className="w-full min-w-[680px] text-sm" data-testid="reconciliation">
            <thead>
              <tr className="border-b border-line text-left text-xs text-muted">
                <th className="px-4 py-3 font-medium">Asset</th>
                <th className="px-4 py-3 text-right font-medium">Users</th>
                <th className="px-4 py-3 text-right font-medium">Fees</th>
                <th className="px-4 py-3 text-right font-medium">Swap clearing</th>
                <th className="px-4 py-3 text-right font-medium">Hot wallet (chain)</th>
                <th className="px-4 py-3 text-right font-medium">Difference</th>
              </tr>
            </thead>
            <tbody>
              {rec.data.map((r) => (
                <tr key={`${r.chain}:${r.asset}`} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 text-fg">
                    {r.asset} <span className="text-muted">· {r.chain}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">
                    {formatAmount(r.userBalances, 8)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{formatAmount(r.fees, 8)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">
                    {formatAmount(r.swapClearing, 8)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">
                    {r.hotWalletOnChain === null ? "—" : formatAmount(r.hotWalletOnChain, 8)}
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-mono tabular-nums ${r.difference?.startsWith("-") ? "text-warning" : "text-fg"}`}
                  >
                    {r.difference === null ? "—" : formatAmount(r.difference, 8)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </section>
  );
}

function ColdModal({
  chain,
  onClose,
  onSent,
}: {
  chain: string | null;
  onClose: () => void;
  onSent: (explorerUrl: string) => void;
}) {
  const config = useConfig();
  const queryClient = useQueryClient();
  const plan = useQuery({
    queryKey: ["cold-plan", chain],
    queryFn: () => api.coldPlan(chain!),
    enabled: chain !== null,
    staleTime: 0,
    retry: false,
  });
  const [asset, setAsset] = useState("");
  const [amount, setAmount] = useState("");
  const [typed, setTyped] = useState("");

  const selected = asset || plan.data?.balances[0]?.asset || "";
  const decimals =
    config.data?.assets.find((a) => a.chain === chain && a.symbol === selected)?.decimals ?? 18;
  const available = plan.data?.balances.find((b) => b.asset === selected)?.balance ?? "0";
  const normalised = normaliseAmountInput(amount, decimals);
  const amountOk =
    !!normalised &&
    compareDecimalStrings(normalised, "0") > 0 &&
    compareDecimalStrings(normalised, available) <= 0;

  return (
    <StepUpModal
      open={chain !== null}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          setAsset("");
          setAmount("");
          setTyped("");
        }
      }}
      title={`Move ${chain ?? ""} funds to cold`}
      description="From the hot wallet to YOUR cold wallet (set in the signer's .env — it can't be changed here)."
      confirmLabel="Send to cold wallet"
      danger
      canConfirm={amountOk && typed === "SEND" && !!plan.data?.coldAddress}
      onConfirm={async (code) => {
        const r = await api.sendToCold(
          chain!,
          { asset: selected, amount: normalised!, confirm: typed },
          code,
        );
        onSent(r.explorerUrl);
        void queryClient.invalidateQueries({ queryKey: qk.hotWallets });
        return r;
      }}
    >
      {plan.isPending && <Skeleton className="h-24" />}
      {plan.error && <Notice tone="danger">{errorMessage(plan.error)}</Notice>}
      {plan.data && (
        <>
          <div className="rounded-field border border-line bg-soft p-3">
            <Row label="Cold wallet">
              {plan.data.coldAddress ? (
                <span className="font-mono text-xs break-all" data-testid="cold-address">
                  {plan.data.coldAddress}
                </span>
              ) : (
                <span className="text-warning">not configured</span>
              )}
            </Row>
            {plan.data.balances.map((b) => (
              <Row key={b.asset} label={`Hot ${b.asset}`}>
                <span className="font-mono">{formatAmount(b.balance, 8)}</span>
              </Row>
            ))}
          </div>
          <Notice tone="warning">Compare the cold address with your hardware wallet before sending.</Notice>
          <Field label="Asset">
            {(p) => (
              <Select {...p} value={selected} onChange={(e) => setAsset(e.target.value)}>
                {plan.data.balances.map((b) => (
                  <option key={b.asset} value={b.asset}>
                    {b.asset}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Amount" hint={`Hot wallet holds ${formatAmount(available, 8)} ${selected}`}>
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="font-mono"
              />
            )}
          </Field>
          <Field label='Type "SEND" to confirm'>
            {(p) => (
              <Input {...p} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
            )}
          </Field>
        </>
      )}
    </StepUpModal>
  );
}
